/**
 * propertyDisplayFields.js — Dérivation centralisée des champs d'affichage d'un bien.
 *
 * Source de vérité unique pour les groupes NON CONFIDENTIELS (surfaces, structurelles,
 * capacités, équipements), filtrés par activité. Utilisé par la fiche publique, la vue
 * interne et (miroir Python) le PDF client/agent.
 *
 * Règle équipements :
 *   - Booléen → coche verte + libellé si vrai ; masqué si faux/vide (jamais "Libellé : oui")
 *   - Valeur (enum/texte) → "Libellé : valeur" ; masqué si vide
 *   - Filtrage par activité : un champ n'apparaît que s'il est pertinent ET vrai
 *   - Sous-attributs REGROUPÉS par famille : Terrasse (type · couverte · chauffée · autorisation),
 *     Extraction (toiture · gaine · Ø mm), Chambre froide (enum ×n), Bar (tireuse),
 *     Électricité (triphasé · kW), Pont roulant (capacité t)
 *
 * ⚠️ EXCLUSIONS confidentielles (jamais public/impression client) :
 *   - date_fin_occupation, procedure (informations_confidentielles), propriétaire,
 *     codes d'accès, tout informations_confidentielles.*
 */

const isYes = (v) => v === true || v === 'oui' || v === 'Oui' || v === 'yes' || v === 'true' || v === 1 || v === '1';

const profilOf = (p) => {
  const bg = p.bien_general || {};
  return (p.profil || bg.profil || (p.taxonomy || {}).profil || '').toString().toLowerCase();
};

// Libellés propres pour les enums stockés sans accents/minuscules (données migrées)
// Exporté pour être partagé par CoreFieldRenderer (options des selects) et la vue interne/publique/PDF.
// Clés : forme normalisée (lowercase, underscore → espace).
export const ENUM_LABELS = {
  negative: 'Négative',
  positive: 'Positive',
  'les deux': 'Les deux',
  fermee: 'Fermée',
  ouverte: 'Ouverte',
  couverte: 'Couverte',
  froide: 'Froide',
  chaude: 'Chaude',
  mixte: 'Mixte',
  reversible: 'Réversible',
  'double flux': 'Double flux',
  gainable: 'Gainable',
  centralisee: 'Centralisée',
  raccordable: 'Raccordable',
  prive: 'Privé',
  privative: 'Privative',
  'domaine public': 'Domaine public',
  aucune: 'Aucune',
  simple: 'Simple',
};

/** Utilitaire partagé : normalise `Negative`/`negative`/`les_deux` → « Négative » / « Les deux ». */
export function labelizeEnum(v) {
  if (v === null || v === undefined || v === '') return '';
  const s = String(v).replace(/_/g, ' ').trim();
  if (!s) return '';
  return ENUM_LABELS[s.toLowerCase()] || s.charAt(0).toUpperCase() + s.slice(1);
}

const cleanEnum = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v).replace(/_/g, ' ').trim();
  if (!s) return '';
  return ENUM_LABELS[s.toLowerCase()] || s.charAt(0).toUpperCase() + s.slice(1);
};

const fmtNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? String(n % 1 === 0 ? Math.round(n) : n) : String(v);
};

const EMPTY_VALUES = ['non', 'aucun', 'aucune'];

/**
 * Équipements & options — liste d'items { label, yes: true } (booléen vrai)
 * ou { label, text: 'valeur' } (champ à valeur / famille groupée). Jamais d'item faux/vide.
 */
export function deriveEquipements(property) {
  const p = property || {};
  const bg = p.bien_general || {};
  const comp = p.composition || {};
  const profil = profilOf(p);
  const isCHR = profil === 'chr';
  const isRetail = profil === 'retail';
  const isBureaux = profil === 'tertiaire' || profil === 'bureaux';
  const isIndus = profil === 'industriel' || profil === 'logistique';
  const noProfil = !profil;
  const gate = (pertinent) => pertinent || noProfil;

  const items = [];
  const seen = new Set();
  const check = (label, v, pertinent = true) => {
    if (gate(pertinent) && isYes(v) && !seen.has(label)) {
      seen.add(label);
      items.push({ label, yes: true });
    }
  };
  const addVal = (label, text) => {
    if (text && !seen.has(label)) {
      seen.add(label);
      items.push({ label, text });
    }
  };
  const val = (label, v) => {
    const s = cleanEnum(v);
    if (s && !EMPTY_VALUES.includes(s.toLowerCase())) addVal(label, s);
  };
  // Famille groupée : 1 qualificatif → "Libellé : valeur" ; ≥2 → chips (quals) pleine largeur
  const addGroup = (label, quals) => {
    if (!quals.length || seen.has(label)) return;
    if (quals.length === 1) {
      addVal(label, quals[0]);
      return;
    }
    seen.add(label);
    items.push({ label, text: quals.join(' · '), quals });
  };

  // Climatisation : type prioritaire ("Climatisation : Réversible"), sinon coche si booléen vrai
  if (comp.climatisation_type) val('Climatisation', comp.climatisation_type);
  else check('Climatisation', comp.climatisation);

  // lot CX — la kitchenette était saisie sans jamais apparaître.
  check('Kitchenette', comp.kitchenette);

  // Fibre optique : oui → coche ; raccordable → "Fibre optique : Raccordable" ; non/vide → masqué
  if (isYes(comp.fibre_optique)) check('Fibre optique', true);
  else if (typeof comp.fibre_optique === 'string' && comp.fibre_optique.trim().toLowerCase() === 'raccordable') {
    addVal('Fibre optique', 'Raccordable');
  }

  // ── Terrasse — entrée groupée "Terrasse : <type> · couverte · chauffée · autorisation" ──
  const terQuals = [];
  const terType = cleanEnum(comp.terrasse_type);
  if (terType && !EMPTY_VALUES.includes(terType.toLowerCase())) terQuals.push(terType);
  if (gate(isCHR) && isYes(comp.terrasse_couverte) && !terQuals.some((q) => q.toLowerCase().includes('couverte'))) terQuals.push('couverte');
  if (gate(isCHR) && isYes(comp.terrasse_chauffee)) terQuals.push('chauffée');
  if (gate(isCHR) && isYes(comp.autorisation_terrasse)) terQuals.push('autorisation');
  if (terQuals.length) addGroup('Terrasse', terQuals);
  else check('Terrasse', bg.terrasse);

  // ── Extraction — entrée groupée "Extraction : toiture · gaine · Ø mm" (CHR) ──
  if (gate(isCHR)) {
    const extQuals = [];
    if (isYes(comp.extraction_toiture)) extQuals.push('toiture');
    if (isYes(comp.gaine_extraction)) extQuals.push('gaine');
    if (comp.extraction_diametre_mm) extQuals.push(`Ø ${fmtNum(comp.extraction_diametre_mm)} mm`);
    const extOn = isYes(comp.extraction_existe) || isYes(comp.extraction) || extQuals.length > 0;
    if (extOn && extQuals.length) addGroup('Extraction', extQuals);
    else if (extOn) check('Extraction', true);
  }

  // ── Chambre froide — enum (Positive/Négative/Les deux) + nombre, ou booléen legacy ──
  if (isYes(comp.chambre_froide)) check('Chambre froide', true, isCHR);
  else {
    const cf = cleanEnum(comp.chambre_froide);
    if (cf && !EMPTY_VALUES.includes(cf.toLowerCase())) {
      const n = comp.nombre_chambres_froides;
      addVal('Chambre froide', n && n > 1 ? `${cf} (×${n})` : cf);
    }
  }

  // PMR (fusion des sources) + communs
  check('Accès PMR', isYes(comp.pmr) || isYes(bg.accessibilite_pmr));
  check('Salle privative', comp.salle_privee);
  check('Ascenseur', comp.ascenseur);
  check("Contrôle d'accès", comp.controle_acces);

  // CHR
  check('Gaz', isYes(comp.gaz_existe) || isYes(comp.gaz), isCHR);

  // ── Bar & Tireuse à bière — deux équipements indépendants (CHR) ──
  // Ne pas fusionner : ce sont deux booléens distincts. Chacun rend une coche indépendante.
  if (gate(isCHR)) {
    check('Bar', comp.bar_existe);
    check('Tireuse à bière', comp.tireuse_biere);
  }

  // ── Électricité — triphasé + puissance groupés (CHR / industriel) ──
  if (gate(isCHR || isIndus)) {
    const tri = isYes(comp.electricite_tri) || isYes(comp.triphase);
    const kw = comp.puissance_kw;
    if (tri && kw) addGroup('Électricité', ['triphasé', `${fmtNum(kw)} kW`]);
    else if (tri) check('Triphasé', true);
    else if (kw) addVal('Puissance électrique', `${fmtNum(kw)} kW`);
  }

  // Retail
  [
    ['angle_rue', 'Angle de rue'],
    ['double_vitrine', 'Double vitrine'],
    ['rideau_metal', 'Rideau métallique'],
    ['alarme', 'Alarme'],
    ['reserve_securisee', 'Réserve sécurisée'],
  ].forEach(([k, l]) => check(l, comp[k], isRetail));

  // Bureaux / Tertiaire
  [
    ['divisible', 'Divisible'],
    ['cablage_rj45', 'Câblage RJ45'],
    ['baie_brassage', 'Baie de brassage'],
    ['faux_plancher', 'Faux plancher'],
    ['faux_plafond', 'Faux plafond'],
  ].forEach(([k, l]) => check(l, comp[k], isBureaux));

  // Industriel / Logistique
  [
    ['plain_pied', 'Plain-pied'],
    ['aire_manoeuvre', 'Aire de manœuvre'],
    ['sprinkler', 'Sprinkler'],
    ['icpe', 'ICPE'],
    ['frigorifique', 'Frigorifique'],
    ['temperature_dirigee', 'Température dirigée'],
  ].forEach(([k, l]) => check(l, comp[k], isIndus));

  // ── Pont roulant — groupé avec la capacité (industriel) ──
  if (gate(isIndus) && isYes(comp.pont_roulant)) {
    if (comp.capacite_pont) addVal('Pont roulant', `${fmtNum(comp.capacite_pont)} t`);
    else check('Pont roulant', true);
  }

  // Champs à valeur restants
  val('Type cuisine', comp.type_cuisine);
  val('Cuisine équipée', bg.cuisine_equipee);
  val('Chauffage', comp.chauffage_type);
  val('Parking', bg.parking_type);

  return items;
}

/**
 * @param {Object} property - Le bien complet
 * @returns {{surfaces: Array, structurelles: Array, capacites: Array, equipements: Array}}
 */
export function derivePropertyFields(property) {
  const p = property || {};
  const bg = p.bien_general || {};
  const comp = p.composition || {};
  const ouv = p.ouverture_fermeture || {};
  const profil = profilOf(p);
  const operation = p.operation || (p.taxonomy || {}).operation;

  const isCHR = profil === 'chr';
  const isBureaux = profil === 'tertiaire' || profil === 'bureaux';
  const isIndus = profil === 'industriel' || profil === 'logistique';
  // iter111 — Nouveaux profils pour projection publique adaptative
  const sousProfil = (p.sous_profil || bg.sous_profil || '').toString().toLowerCase();
  const isHotel = ['hotel', 'hotel_meuble'].includes(sousProfil);
  const isTabac = sousProfil === 'tabac_presse_pmu';
  const isLiberaux = profil === 'services_liberaux';
  const isMedical = ['cabinet_medical', 'cabinet_dentaire', 'cabinet_paramedical', 'laboratoire_analyses'].includes(sousProfil);
  const isJuridique = ['cabinet_avocat', 'cabinet_notaire', 'cabinet_comptable', 'cabinet_architecte'].includes(sousProfil);
  const exp = p.exploitation || {};
  const objet = (p.objet || (p.taxonomy || {}).objet || '').toString().toLowerCase();
  const isLicenceBien = objet === 'licence_exploitation' || objet === 'licence' || operation === 'licence' || profil === 'licence';
  const fmtEur = (v) => (v != null ? `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(Number(v))}\u00A0€` : null);

  // ── Surfaces (toujours pertinentes si renseignées) ──
  // Note : pour un CHR, "Surface vente" n'a pas de sens métier — la "Surface salle" couvre le besoin.
  // On la masque donc pour ce profil (le champ reste éditable, il n'est simplement pas rendu).
  const showSurfaceVente = !isCHR;
  const surfaces = [
    { label: 'Surface totale', value: comp.superficie_totale, unit: 'm²', highlight: true },
    { label: 'Surface vente', value: showSurfaceVente ? comp.surface_vente : null, unit: 'm²' },
    { label: 'Surface salle', value: comp.surface_salle, unit: 'm²' },
    { label: 'Cuisine', value: comp.surface_cuisine, unit: 'm²' },
    { label: 'Surface bureaux', value: comp.bureaux_m2 || comp.surface_bureaux, unit: 'm²' },
    { label: 'Bureaux fermés', value: comp.surface_bureaux_fermes, unit: 'm²' },
    { label: 'Open space', value: comp.surface_open_space, unit: 'm²' },
    { label: 'Surface salles de réunion', value: comp.surface_salles_reunion, unit: 'm²' },
    { label: 'Accueil', value: comp.surface_accueil, unit: 'm²' },
    { label: 'Réserve', value: comp.reserve, unit: 'm²' },
    { label: 'Stockage', value: comp.surface_stockage, unit: 'm²' },
    { label: 'Production', value: comp.surface_production, unit: 'm²' },
    { label: 'Archives', value: comp.surface_archives, unit: 'm²' },
    { label: 'Mezzanine', value: comp.surface_mezzanine, unit: 'm²' },
    { label: 'Kitchenette', value: comp.surface_kitchenette, unit: 'm²' },
    { label: 'Terrasse', value: comp.terrasse_m2, unit: 'm²' },
    { label: 'Surface extérieure', value: comp.surface_exterieure, unit: 'm²' },
  ].filter((s) => s.value);

  // ── Caractéristiques structurelles (non confidentielles) ──
  // NB : les booléens (PMR, terrasse) sont désormais dans `equipements` (règle coche verte).
  const statutBien = bg.occupe === true ? 'Occupé' : bg.occupe === false ? 'Libre' : (bg.statut_bien || null);
  const structurelles = [
    { label: 'Statut du bien', value: statutBien },
    { label: "Ancienneté activité", value: bg.anciennete_activite || null },
    { label: 'Ancienneté licence', value: bg.anciennete_licence || null },
    { label: 'Horaires', value: ouv.horaires_ouverture_fermeture || null },
    // lot CX — saisis depuis toujours, jamais affichés : un acquéreur veut savoir quand ça ferme.
    { label: 'Fermeture hebdomadaire', value: ouv.fermeture_hebdomadaire || null },
    { label: 'Congés annuels', value: ouv.conges_fermeture_saisonniere || null },
    { label: 'Logement attenant', value: comp.appartement_prive || null },
    { label: "Type d'emplacement", value: comp.emplacement_label || bg.emplacement_label || null },
    { label: 'Hauteur plafond', value: comp.hauteur_plafond ? `${comp.hauteur_plafond} m` : null },
    { label: 'Hauteur libre', value: comp.hauteur_libre ? `${comp.hauteur_libre} m` : null },
    { label: 'Linéaire de façade', value: comp.lineaire_facade ? `${comp.lineaire_facade} m` : null },
    { label: 'Façade', value: comp.facade ? `${comp.facade}${typeof comp.facade === 'number' ? ' m' : ''}` : null },
    { label: 'Étage', value: bg.etage || comp.etage || null },
    { label: 'Vitrine', value: comp.vitrine || null },
    { label: 'Classement ERP', value: bg.classement_erp || null },
    { label: 'Transports à proximité', value: bg.transports_proximite || null },
    { label: 'Parking à proximité', value: bg.parking_proximite || null },
    { label: 'Flux piéton', value: bg.flux_pieton || null },
    // Redevance terrasse (CHR) — champ saisi mais oublié en affichage jusqu'ici
    {
      label: 'Redevance terrasse',
      value: comp.redevance_terrasse_an
        ? `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(Number(comp.redevance_terrasse_an))}\u00A0€/an`
        : null,
    },
    // iter107 — Hôtellerie & réception (argument commercial public)
    {
      label: 'Prix moyen /nuitée',
      value: comp.prix_moyen_nuitee
        ? `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(Number(comp.prix_moyen_nuitee))}\u00A0€`
        : null,
    },
    {
      label: 'Réception',
      value: comp.type_reception === 'Horaires définis'
        ? (comp.horaires_reception || 'Horaires définis')
        : (comp.type_reception || null),
    },
    // Classement hôtelier (Atout France)
    { label: 'Classement hôtel', value: (p.exploitation && p.exploitation.classement_hotel) || null },
    // Saisonnalité
    { label: 'Saisonnalité', value: (p.exploitation && p.exploitation.saisonnalite) || null },
    { label: 'Période d\'ouverture', value: (p.exploitation && p.exploitation.periode_ouverture) || null },
    // iter118 — clés d'exploitation whitelistées : elles étaient diffusées par l'API
    // sans jamais être affichées. Règle : une clé publique a toujours une surface d'affichage.
    { label: "Taux d'occupation", value: exp.taux_occupation_moyen != null ? `${fmtNum(exp.taux_occupation_moyen)}\u00A0%` : null },
    { label: "Normes d'hygiène / HACCP", value: cleanEnum(exp.hygiene_haccp) || null },
    {
      label: 'Laboratoire',
      value: isYes(exp.laboratoire_present)
        ? (exp.laboratoire_surface_m2 ? `Oui — ${fmtNum(exp.laboratoire_surface_m2)} m²` : 'Oui')
        : null,
    },
    // Le FAIT d'être sous franchise seulement (redevance et échéance restent internes)
    { label: 'Sous franchise / réseau', value: isYes(exp.sous_franchise) ? 'Oui' : null },
    // Le TYPE d'agrément uniquement (agrement_reference reste interne)
    { label: "Type d'agrément", value: exp.agrement_type || null },
    // Coût mensuel de la licence = information d'offre (biens licence)
    {
      label: 'Redevance mensuelle',
      value: (isLicenceBien && exp.licence_redevance_mensuelle)
        ? `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(Number(exp.licence_redevance_mensuelle))}\u00A0€/mois`
        : null,
    },
  ].filter((c) => c.value);

  // ── Capacités (chiffres) — enrichies par profil (iter111) ──
  // iter113 — une donnée = une seule ligne : les sièges intérieur/terrasse ont plusieurs
  // stockages historiques (couverts_* / places_assises_* / places_*) et un libellé qui
  // dépend du métier (CHR → « Couverts », sinon « Places »).
  const seatsInt = comp.couverts_interieur ?? comp.places_assises_interieur ?? comp.places_interieur ?? null;
  const seatsTer = comp.couverts_terrasse ?? comp.places_assises_terrasse ?? comp.places_terrasse ?? null;
  const capacites = [
    { label: 'Chambres (hôtel)', value: (p.exploitation && p.exploitation.nombre_chambres) || null },
    { label: 'Restaurants', value: comp.nombre_restaurants },
    { label: 'Niveaux', value: comp.nombre_niveaux },
    { label: 'Couverts (total)', value: comp.nombre_couverts_total },
    { label: isCHR ? 'Couverts intérieur' : 'Places intérieur', value: seatsInt },
    { label: isCHR ? 'Couverts terrasse' : 'Places terrasse', value: seatsTer },
    { label: 'Postes de travail', value: comp.postes_travail },
    { label: 'Salles de réunion', value: comp.salles_reunion_nombre || comp.nb_salles_reunion },
    { label: 'Places parking', value: comp.parking_places },
    { label: 'Quais de chargement', value: comp.quais_chargement },
    { label: 'Salles', value: comp.nombre_salles && comp.nombre_salles > 0 ? comp.nombre_salles : null },
    { label: 'Employés', value: comp.nombre_employes && comp.nombre_employes > 0 ? comp.nombre_employes : null },
    // iter111 — Cabinet libéral (médical / juridique) : bloc métier en public
    ...(isLiberaux ? [
      { label: 'Nombre de pièces', value: comp.nb_pieces },
      { label: 'Nombre de bureaux', value: comp.nb_bureaux },
      { label: "Salle d'attente", value: comp.salle_attente ? 'Oui' : null },
      { label: 'Secrétariat / accueil', value: comp.secretariat_accueil ? 'Oui' : null },
      { label: 'Sanitaires', value: comp.nb_sanitaires },
    ] : []),
    ...(isMedical ? [
      { label: 'Salles de consultation', value: comp.nb_salles_consultation },
      { label: 'Salles de soins', value: comp.nb_salles_soins },
      { label: 'Fauteuils dentaires', value: comp.nb_fauteuils_dentaires },
      { label: 'Salle de stérilisation', value: comp.salle_sterilisation ? 'Oui' : null },
      { label: 'Radio panoramique', value: comp.radio_panoramique ? 'Oui' : null },
      { label: 'Normes médicales', value: comp.normes_medicales ? 'Oui' : null },
      { label: 'PMR obligatoire', value: comp.pmr_obligatoire ? 'Oui' : null },
    ] : []),
    ...(isJuridique ? [
      { label: 'Salle de signature', value: comp.salle_signature ? 'Oui' : null },
      { label: 'Archives sécurisées', value: comp.archives_securisees ? 'Oui' : null },
      { label: 'Coffre-fort', value: comp.coffre_fort ? 'Oui' : null },
      { label: 'Insonorisation', value: comp.insonorisation ? 'Oui' : null },
    ] : []),
    // iter111 — CHR (restaurant) : type de cuisine
    ...(isCHR && !isHotel ? [
      { label: 'Type de cuisine', value: comp.cuisine_type || comp.type_cuisine },
      { label: 'Bar', value: comp.bar_existe ? 'Oui' : null },
      { label: 'Tireuse bière', value: comp.tireuse_biere ? 'Oui' : null },
      { label: 'Extraction', value: (comp.extraction_existe || comp.extraction) ? 'Oui' : null },
    ] : []),
  ].filter((c) => c.value)
    // garde-fou : jamais deux fois le même libellé (registres parallèles par profil)
    .filter((c, i, arr) => arr.findIndex((x) => x.label === c.label) === i);
  const equipements = deriveEquipements(p);

  // lot1 — Commissions (tabac / presse / Loto-FDJ / PMU) : bloc dédié. Un montant en euros
  // n'est pas une « capacité » ; sur une vente de murs, ce n'est pas l'objet de la vente.
  const commissions = (isTabac && !['vente_immo', 'vente_murs'].includes(operation) ? [
    { label: 'Commission tabac /an', value: fmtEur(exp.commission_tabac_an) },
    { label: 'Commission presse /an', value: fmtEur(exp.commission_presse_an) },
    { label: 'Commission Loto / FDJ /an', value: fmtEur(exp.commission_loto_fdj_an) },
    { label: 'Commission PMU /an', value: fmtEur(exp.commission_pmu_an) },
  ] : []).filter((c) => c.value);

  // Texte libre d'exploitation (whitelisté public) — rendu dans un bloc dédié,
  // retours à la ligne respectés. Jamais mélangé aux paires libellé/valeur.
  const specificites = (exp.specificites && String(exp.specificites).trim()) || null;

  return { surfaces, structurelles, capacites, equipements, commissions, specificites, ctx: { isCHR, isBureaux, isIndus, isLicenceBien, operation } };
}

export default derivePropertyFields;
